import { z } from "zod";
import { AddressFields, AddressInput, AddressPatch } from "../common/address.js";
import { CountryCode } from "../common/country.js";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
export const AgreementType = z
    .enum(["platform_terms", "buyer_terms", "seller_agreement"])
    .meta({ id: "AgreementType" });
export const VerificationStatus = z
    .enum(["submitted", "under_review", "verified", "rejected"])
    .meta({ id: "VerificationStatus" });
export const AgreementStatus = z
    .object({
    type: AgreementType,
    currentVersion: z.string(),
    acceptedVersion: z.string().nullable().describe("The latest version this enterprise accepted"),
    required: z
        .boolean()
        .describe("Needed before trading: platform and buyer terms always, the seller agreement once selling is on"),
})
    .meta({ id: "AgreementStatus" });
export const Enterprise = z
    .object({
    id: Id,
    legalName: z.string(),
    registrationNumber: z.string(),
    country: CountryCode,
    displayName: LocalizedText,
    industry: z.string().nullable(),
    website: z.string().nullable(),
    verificationStatus: VerificationStatus,
    suspension: z.object({ at: Timestamp, reason: z.string().nullable() }).nullable(),
    buyerEnabled: z.boolean(),
    sellerEnabled: z.boolean(),
    canTrade: z.boolean().describe("Verified, not suspended, and every required agreement accepted"),
    agreements: z.array(AgreementStatus),
    version: z.number().int().positive(),
    createdAt: Timestamp,
})
    .meta({ id: "Enterprise" });
const Website = z.url({ protocol: /^https?$/ }).max(200);
export const createEnterprise = defineRoute({
    id: "identity.enterprises.create",
    method: "POST",
    path: "/v1/enterprises",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: ["idem"],
    summary: "Create an enterprise; the caller becomes its admin and switches to it next. A registration number already used in that country is refused and its admins are told. A country or registered address Civentia does not serve is refused (`COUNTRY_NOT_SERVED`), and a market not open yet (`MARKET_NOT_OPEN`; ADR 0025, card 127)",
    prd: ["ENT-1"],
    request: {
        body: z.object({
            legalName: z.string().trim().min(1).max(200),
            registrationNumber: z
                .string()
                .trim()
                .min(1)
                .max(50)
                .describe("Company registration number, such as a Singapore UEN; stored upper-case without spaces"),
            country: CountryCode,
            displayName: LocalizedText,
            industry: z.string().trim().min(1).max(100).optional(),
            website: Website.optional(),
            address: AddressInput.describe("Registered address; becomes the default delivery address and shipping origin"),
        }),
    },
    responses: { 201: { description: "The new enterprise", schema: Enterprise } },
    errors: [
        "AUTH_REQUIRED",
        "VALIDATION_FAILED",
        "DUPLICATE_REGISTRATION",
        "COUNTRY_NOT_SERVED",
        "MARKET_NOT_OPEN",
    ],
});
export const getEnterprise = defineRoute({
    id: "identity.enterprise.get",
    method: "GET",
    path: "/v1/enterprise",
    surface: "tenant",
    auth: "user",
    permission: "directory:read",
    marks: ["tenant"],
    summary: "The active enterprise: profile, verification, capabilities, suspension, agreements",
    prd: ["ENT-3", "ENT-5"],
    responses: { 200: { description: "The active enterprise", schema: Enterprise } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const updateEnterprise = defineRoute({
    id: "identity.enterprise.update",
    method: "PATCH",
    path: "/v1/enterprise",
    surface: "tenant",
    auth: "user",
    permission: "enterprise:manage",
    marks: ["tenant"],
    summary: "Edit profile fields that do not need re-verification; legal name, number, and country change only through verification",
    request: {
        body: z.object({
            displayName: LocalizedText.optional(),
            industry: z.string().trim().min(1).max(100).nullable().optional(),
            website: Website.nullable().optional(),
        }),
    },
    responses: { 200: { description: "The updated enterprise", schema: Enterprise } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const AgreementVersion = z
    .object({
    type: AgreementType,
    version: z.string(),
    effectiveFrom: Timestamp,
    documentFileId: Id.nullable(),
})
    .meta({ id: "AgreementVersion" });
export const currentAgreements = defineRoute({
    id: "identity.agreements.current",
    method: "GET",
    path: "/v1/agreements/current",
    surface: "tenant",
    auth: "none",
    permission: "none",
    marks: [],
    summary: "Current versions of the platform terms, buyer terms, and seller agreement",
    prd: ["ENT-5"],
    responses: {
        200: { description: "Current versions", schema: z.object({ items: z.array(AgreementVersion) }) },
    },
});
export const acceptAgreement = defineRoute({
    id: "identity.agreements.accept",
    method: "POST",
    path: "/v1/enterprise/agreements/{type}/accept",
    surface: "tenant",
    auth: "user",
    permission: "agreement:accept",
    marks: ["tenant"],
    summary: "Accept the current version of an agreement for the active enterprise; accepting it again is harmless",
    prd: ["ENT-5"],
    request: {
        params: z.object({ type: AgreementType }),
        body: z.object({
            version: z.string().min(1).describe("The version the user read; must be the current one"),
        }),
    },
    responses: { 200: { description: "The enterprise with its agreement status", schema: Enterprise } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "INVALID_TRANSITION"],
});
export const Address = z
    .object({
    id: Id,
    label: z.string(),
    country: CountryCode,
    fields: AddressFields,
    contactName: z.string().nullable(),
    contactPhone: z.string().nullable(),
    isDefaultDelivery: z.boolean(),
    isDefaultOrigin: z.boolean(),
    updatedAt: Timestamp,
})
    .meta({ id: "Address" });
export const listAddresses = defineRoute({
    id: "identity.addresses.list",
    method: "GET",
    path: "/v1/enterprise/addresses",
    surface: "tenant",
    auth: "user",
    permission: "directory:read",
    marks: ["tenant"],
    summary: "The enterprise's address book, defaults first",
    prd: ["ORD-7"],
    responses: { 200: { description: "Addresses", schema: z.object({ items: z.array(Address) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const createAddress = defineRoute({
    id: "identity.addresses.create",
    method: "POST",
    path: "/v1/enterprise/addresses",
    surface: "tenant",
    auth: "user",
    permission: "address:manage",
    marks: ["tenant"],
    summary: "Add an address with fields validated for its country; a country or region Civentia does not serve is refused",
    prd: ["ORD-7"],
    request: { body: AddressInput },
    responses: { 201: { description: "The new address", schema: Address } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "VALIDATION_FAILED",
        "COUNTRY_NOT_SERVED",
    ],
});
export const updateAddress = defineRoute({
    id: "identity.addresses.update",
    method: "PATCH",
    path: "/v1/enterprise/addresses/{addressId}",
    surface: "tenant",
    auth: "user",
    permission: "address:manage",
    marks: ["tenant"],
    summary: "Edit an address or make it a default; orders keep the snapshot they took",
    prd: ["ORD-7"],
    request: { params: z.object({ addressId: Id }), body: AddressPatch },
    responses: { 200: { description: "The updated address", schema: Address } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "VALIDATION_FAILED",
        "NOT_FOUND",
        "COUNTRY_NOT_SERVED",
    ],
});
export const deleteAddress = defineRoute({
    id: "identity.addresses.delete",
    method: "DELETE",
    path: "/v1/enterprise/addresses/{addressId}",
    surface: "tenant",
    auth: "user",
    permission: "address:manage",
    marks: ["tenant"],
    summary: "Delete an address; orders keep the snapshot they took",
    prd: ["ORD-7"],
    request: { params: z.object({ addressId: Id }) },
    responses: { 204: { description: "Deleted" } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const VerificationSession = z
    .discriminatedUnion("provider", [
    z.object({
        provider: z.literal("sumsub"),
        accessToken: z.string().describe("Opens Sumsub's hosted business verification in the client"),
        expiresIn: z.number().int().positive().describe("Seconds until the access token expires"),
    }),
    z.object({
        provider: z.literal("manual").describe("Verified by hand: the case is with the reviewers (ADR 0026)"),
    }),
])
    .meta({ id: "VerificationSession" });
export const Verification = z
    .object({
    status: VerificationStatus,
    provider: z
        .enum(["sumsub", "manual"])
        .describe("How verification runs now: Sumsub's hosted flow, or by hand by Civentia's reviewers (ADR 0026)"),
    case: z
        .object({
        id: Id,
        providerStatus: z
            .string()
            .nullable()
            .describe("Sumsub's review status, such as pending or completed"),
        providerAnswer: z.enum(["approved", "rejected"]).nullable(),
        resubmission: z
            .object({ labels: z.array(z.string()), comment: z.string().nullable() })
            .nullable()
            .describe("What Sumsub asks the enterprise to fix and resubmit in the hosted flow"),
        decision: z
            .enum(["approved", "rejected", "info_requested"])
            .nullable()
            .describe("The platform reviewer's decision"),
        decisionReason: z.string().nullable(),
        decidedAt: Timestamp.nullable(),
        updatedAt: Timestamp,
    })
        .nullable(),
})
    .meta({ id: "Verification" });
export const startVerification = defineRoute({
    id: "identity.verification.start",
    method: "POST",
    path: "/v1/enterprise/verification",
    surface: "tenant",
    auth: "user",
    permission: "enterprise:manage",
    marks: ["tenant", "idem"],
    summary: "Start or resume business verification. With Sumsub, get a token for its hosted flow; identity documents go to Sumsub, never to the platform. Verified by hand (ADR 0026), the enterprise goes under review at once, with no documents asked",
    prd: ["ENT-2"],
    responses: {
        200: {
            description: "A session for the hosted flow, or the case with the reviewers",
            schema: VerificationSession,
        },
    },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "INVALID_TRANSITION",
        "PROVIDER_UNAVAILABLE",
        "COUNTRY_NOT_SERVED",
        "MARKET_NOT_OPEN",
    ],
});
export const getVerification = defineRoute({
    id: "identity.verification.get",
    method: "GET",
    path: "/v1/enterprise/verification",
    surface: "tenant",
    auth: "user",
    permission: "enterprise:manage",
    marks: ["tenant"],
    summary: "Verification status, the latest case, the reviewer's decision, and what to resubmit",
    prd: ["ENT-2"],
    responses: { 200: { description: "Verification", schema: Verification } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const enterpriseRoutes = [
    createEnterprise,
    getEnterprise,
    updateEnterprise,
    currentAgreements,
    acceptAgreement,
    listAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
    startVerification,
    getVerification,
];
//# sourceMappingURL=enterprise.routes.js.map